import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPinned, Pencil, Plus, ScanLine, Search, Table2, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, Field, PageHeader, StatusPill } from "@/components/common";
import { CoordinatePicker, getGps } from "@/components/MemberMap";
import { CameraCapture } from "@/components/CameraCapture";
import { isResortOn, usePoll } from "@/lib/live";
import { DatePicker } from "@/components/TxTable";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { onlyDigits, rupiah, todayStr } from "@/lib/format";

const EMPTY = { nik: "", name: "", address: "", phone: "", join_date: todayStr(), resort_id: "", lat: null, lng: null };

const ResortPicker = ({ value, onChange }) => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/resorts").then((r) => {
      const l = r.data.resorts.filter((x) => x.active && (user.role !== "petugas" || (x.petugas?.id === user.id && isResortOn(x.id))));
      setList(l);
      if (l.length === 1 && !value) onChange(l[0].id);
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-11" data-testid="member-resort-select"><SelectValue placeholder="Pilih resort" /></SelectTrigger>
      <SelectContent>
        {list.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`member-resort-${r.id}`}>{r.name}{r.petugas ? ` · ${r.petugas.name}` : ""}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const KtpScan = ({ onRead }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const scan = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "ktp.jpg");
      const { data } = await api.post("/ocr/ktp", fd);
      setOpen(false); setDone(true);
      toast.success("Data KTP terbaca, mengambil lokasi GPS...");
      onRead({ ...data, ktp_blob: blob });
      getGps().then((c) => onRead(c)).catch(() => toast.error("Aktifkan GPS untuk titik lokasi"));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="ktp-scan-button"
        className={`w-full rounded-2xl border-2 border-dashed p-4 flex items-center gap-3 text-left transition-colors ${done ? "border-primary bg-primary/5" : "hover:bg-muted"}`}>
        <span className="h-11 w-11 rounded-xl bg-primary text-primary-foreground grid place-items-center shrink-0"><ScanLine className="h-5 w-5" /></span>
        <span><span className="block font-semibold">{done ? "KTP berhasil dibaca" : "Langkah 1: Scan KTP"}</span><span className="block text-xs text-muted-foreground">{done ? "Ketuk untuk scan ulang" : "NIK, nama & alamat terisi otomatis"}</span></span>
      </button>
      <CameraCapture open={open} onOpenChange={setOpen} onConfirm={scan} busy={busy} title="Scan KTP" description="Posisikan KTP di dalam bingkai, pastikan tulisan terbaca jelas." initialFacing="environment" frame="card" />
    </>
  );
};

export const MemberForm = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...initial, resort_id: initial.resort_id || "" } : { ...EMPTY, join_date: todayStr() }); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!initial && f.nik.length !== 16) { toast.error("NIK harus 16 digit"); return; }
    if (!f.resort_id) { toast.error("Pilih resort anggota"); return; }
    setBusy(true);
    try {
      const body = { name: f.name, address: f.address, phone: f.phone, join_date: f.join_date, resort_id: f.resort_id, lat: f.lat ?? null, lng: f.lng ?? null };
      const { data } = initial ? await api.put(`/members/${initial.nik}`, body) : await api.post("/members", { ...body, nik: f.nik });
      if (!initial && f.ktp_blob) {
        const fd = new FormData();
        fd.append("file", f.ktp_blob, "ktp.jpg");
        await api.post(`/members/${f.nik}/ktp`, fd).catch(() => toast.error("Foto KTP gagal disimpan, unggah ulang di rincian anggota"));
      }
      toast.success(initial ? "Data anggota diperbarui" : "Anggota terdaftar");
      onOpenChange(false); onSaved(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="member-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Ubah Data Anggota" : "Anggota Baru"}</DialogTitle><DialogDescription>NIK dipakai sebagai ID utama anggota dan tidak dapat diubah.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!initial && <KtpScan onRead={(d) => setF((x) => ({ ...x, ...d }))} />}
          <Field label="NIK (16 digit)"><Input required disabled={Boolean(initial)} inputMode="numeric" maxLength={16} className="h-11 font-mono tracking-wider" value={f.nik} onChange={(e) => setF({ ...f, nik: onlyDigits(e.target.value).slice(0, 16) })} data-testid="member-nik-input" /></Field>
          <Field label="Nama Lengkap"><Input required className="h-11" value={f.name} onChange={set("name")} data-testid="member-name-input" /></Field>
          <Field label="Alamat"><Textarea required rows={2} value={f.address} onChange={set("address")} data-testid="member-address-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="No. HP / WhatsApp (opsional)"><Input inputMode="tel" className="h-11" value={f.phone} onChange={(e) => setF({ ...f, phone: onlyDigits(e.target.value) })} data-testid="member-phone-input" /></Field>
            <Field label="Tanggal Bergabung"><DatePicker value={f.join_date} onChange={(v) => setF((x) => ({ ...x, join_date: v }))} testId="member-join-date-input" /></Field>
          </div>
          {initial && <Field label="Nomor Pinjaman"><Input disabled className="h-11 font-mono" value={initial.loan_no || "Belum ada pinjaman"} data-testid="member-loan-no-input" /></Field>}
          <Field label="Resort" hint="Petugas lapangan anggota mengikuti resort."><ResortPicker value={f.resort_id} onChange={(v) => setF({ ...f, resort_id: v })} /></Field>
          <Field label="Titik Koordinat Alamat"><CoordinatePicker lat={f.lat} lng={f.lng} onChange={(c) => setF((x) => ({ ...x, ...c }))} /></Field>
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full h-11 px-6" data-testid="member-form-submit">{busy ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const RowActions = ({ m, canManage, canEdit, canDelete, onEdit, onToggle, onDelete }) => (
  <div className="flex items-center justify-end gap-1.5">
    {canEdit && <Button variant="outline" size="sm" className="h-9 rounded-full" onClick={() => onEdit(m)} data-testid={`member-edit-${m.nik}`}><Pencil className="h-3.5 w-3.5 mr-1" />Ubah</Button>}
    {canManage && <Switch checked={m.active} onCheckedChange={(v) => onToggle(m, v)} data-testid={`member-toggle-${m.nik}`} aria-label="Status anggota" />}
    {canDelete && <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-full text-rose-600" onClick={() => onDelete(m)} aria-label="Hapus" data-testid={`member-delete-${m.nik}`}><Trash2 className="h-4 w-4" /></Button>}
    <Button asChild variant="ghost" size="sm" className="h-9 rounded-full"><Link to={`/anggota/${m.nik}`} data-testid={`member-detail-link-${m.nik}`}>Rincian<ChevronRight className="h-4 w-4 ml-0.5" /></Link></Button>
  </div>
);

const MemberTable = ({ items, ...props }) => (
  <Table>
    <TableHeader><TableRow><TableHead className="pl-6">NIK</TableHead><TableHead>No. Pinjaman</TableHead><TableHead>Nama</TableHead><TableHead>Resort · Petugas</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Saldo Simpanan</TableHead><TableHead className="text-right">Jumlah Pinjaman</TableHead><TableHead className="text-right">Sisa Pinjaman</TableHead><TableHead /></TableRow></TableHeader>
    <TableBody>
      {items.map((m) => (
        <TableRow key={m.nik} data-testid={`member-row-${m.nik}`}>
          <TableCell className="pl-6 font-mono text-xs">{m.nik}</TableCell>
          <TableCell className="font-mono text-xs whitespace-nowrap" data-testid={`member-loan-no-${m.nik}`}>{m.loan_no || "-"}</TableCell>
          <TableCell className="font-medium">{m.name}</TableCell>
          <TableCell className="text-sm">{m.resort_name || "Tanpa resort"}<p className="text-xs text-muted-foreground">{m.petugas_name || "-"}</p></TableCell>
          <TableCell><StatusPill active={m.active} /></TableCell>
          <TableCell className="text-right font-mono">{rupiah(m.savings_balance)}</TableCell>
          <TableCell className="text-right font-mono" data-testid={`member-principal-${m.nik}`}>{rupiah(m.loan_principal)}</TableCell>
          <TableCell className="text-right font-mono" data-testid={`member-outstanding-${m.nik}`}>{rupiah(m.outstanding)}</TableCell>
          <TableCell className="pr-4"><RowActions m={m} {...props} /></TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const MemberCards = ({ items, ...props }) => (
  <div className="divide-y">
    {items.map((m) => (
      <div key={m.nik} className="p-4 space-y-3" data-testid={`member-card-${m.nik}`}>
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-primary/12 text-primary grid place-items-center font-bold shrink-0">{m.name[0]}</span>
          <div className="min-w-0 flex-1"><p className="font-medium truncate">{m.name}</p><p className="font-mono text-xs text-muted-foreground">{m.nik}{m.loan_no ? ` · ${m.loan_no}` : ""}</p></div>
          <StatusPill active={m.active} />
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-xl bg-muted/50 p-2.5"><p className="text-muted-foreground">Simpanan</p><p className="font-mono font-semibold text-sm">{rupiah(m.savings_balance)}</p></div>
          <div className="rounded-xl bg-muted/50 p-2.5"><p className="text-muted-foreground">Sisa Pinjaman</p><p className="font-mono font-semibold text-sm">{rupiah(m.outstanding)}</p></div>
        </div>
        <RowActions m={m} {...props} />
      </div>
    ))}
  </div>
);

const PetugasMemberCards = ({ groups }) => (
  <div className="space-y-5">
    {groups.map(([name, rows]) => (
      <div key={name} data-testid={`member-group-${name}`}>
        <p className="font-heading font-semibold mb-2 flex items-center gap-2"><MapPinned className="h-4 w-4 text-primary" />{name}<span className="rounded-full bg-primary/12 text-primary text-xs px-2 py-0.5">{rows.length} anggota</span></p>
        <div className="space-y-2">
          {rows.map((m) => (
            <div key={m.nik} className="rounded-2xl border bg-card p-3.5 flex items-center gap-3" data-testid={`member-card-${m.nik}`}>
              <span className="h-10 w-10 rounded-full bg-primary/12 text-primary grid place-items-center font-bold shrink-0">{m.name[0]}</span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{m.name}</p>
                <p className="font-mono text-[11px] text-muted-foreground">{m.nik}</p>
                <p className="text-xs mt-0.5">Sisa <span className="font-mono font-semibold" data-testid={`member-outstanding-${m.nik}`}>{rupiah(m.outstanding)}</span></p>
              </div>
              <Button asChild size="sm" variant="outline" className="h-9 rounded-full shrink-0"><Link to={`/anggota/${m.nik}`} data-testid={`member-detail-link-${m.nik}`}>Detail</Link></Button>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default function MembersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const canCreate = ["admin_cabang", "kasir", "petugas"].includes(user.role);
  const canManage = ["admin_cabang", "kepala_bagian"].includes(user.role);
  const [del, setDel] = useState(null);
  const onDelete = async () => {
    try { await api.delete(`/members/${del.nik}`); toast.success(`${del.name} dihapus`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(() => {
    api.get("/members", { params: q ? { q } : {} }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  usePoll(load);
  const isPetugas = user.role === "petugas";
  const [view, setView] = useState("card");
  const onToggle = async (m, active) => {
    try { await api.patch(`/members/${m.nik}/status`, { active }); toast.success(`${m.name} ${active ? "diaktifkan" : "dinonaktifkan"}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onEdit = (m) => { setEditing(m); setOpen(true); };
  const props = { canManage, canEdit: ["admin_cabang", "kepala_bagian", "kasir"].includes(user.role), canDelete: user.role === "admin_cabang", onEdit, onToggle, onDelete: setDel };
  const groups = Object.entries((items || []).reduce((g, m) => { (g[m.resort_name || "Tanpa Resort"] ||= []).push(m); return g; }, {})).sort(([a], [b]) => a.localeCompare(b));

  if (isPetugas) {
    return (
      <div>
        <h1 className="font-heading text-2xl font-bold mb-4">Anggota</h1>
        <div className="flex items-center gap-2 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / NIK" className="h-11 pl-10 rounded-full" data-testid="members-search-input" />
          </div>
          <Button className="h-11 rounded-full px-4 shrink-0" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-member-button"><Plus className="h-4 w-4 mr-1" />Anggota</Button>
          <Button variant={view === "table" ? "default" : "outline"} className="h-11 w-11 p-0 rounded-full shrink-0" onClick={() => setView(view === "table" ? "card" : "table")} aria-label="Tampilan tabel" data-testid="members-table-toggle"><Table2 className="h-4 w-4" /></Button>
        </div>
        {items === null ? <p className="text-muted-foreground">Memuat...</p> : items.length === 0 ? (
          <EmptyState icon={Users} title={q ? "Tidak ditemukan" : "Belum ada anggota"} text={q ? "Coba kata kunci lain." : "Tambahkan anggota pertama di resort aktif."} testId="members-empty" />
        ) : view === "card" ? <PetugasMemberCards groups={groups} /> : (
          <div className="space-y-6">
            {groups.map(([name, rows]) => (
              <div key={name} className="panel !p-0 overflow-hidden" data-testid={`member-table-group-${name}`}>
                <p className="font-heading font-semibold px-5 pt-4 pb-2">{name} <span className="text-xs font-normal text-muted-foreground">{rows.length} anggota</span></p>
                <div className="overflow-x-auto"><MemberTable items={rows} {...props} /></div>
              </div>
            ))}
          </div>
        )}
        <MemberForm open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader eyebrow="Data Master" title="Anggota" subtitle="Daftar anggota dengan saldo simpanan dan sisa pinjaman berjalan."
        action={canCreate && <Button className="rounded-full h-11 px-6" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-member-button"><Plus className="h-4 w-4 mr-2" />Anggota Baru</Button>} />
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari NIK atau nama..." className="h-11 pl-11 rounded-full" data-testid="members-search-input" />
      </div>
      {items === null ? <p className="text-muted-foreground">Memuat...</p> : items.length === 0 ? (
        <EmptyState icon={Users} title={q ? "Tidak ditemukan" : "Belum ada anggota"} text={q ? "Coba kata kunci lain." : "Daftarkan anggota pertama kantor cabang ini."} testId="members-empty" />
      ) : (
        <div className="space-y-6">
          {groups.map(([name, rows]) => (
            <div key={name} className="panel !p-0 overflow-hidden" data-testid={`member-group-${name}`}>
              <p className="font-heading font-semibold px-6 pt-5 pb-2 flex items-center gap-2"><MapPinned className="h-4 w-4 text-primary" />{name}<span className="text-xs font-normal text-muted-foreground">{rows.length} anggota</span></p>
              <div className="hidden md:block overflow-x-auto"><MemberTable items={rows} {...props} /></div>
              <div className="md:hidden"><MemberCards items={rows} {...props} /></div>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog open={Boolean(del)} onOpenChange={(o) => !o && setDel(null)} title="Hapus Anggota" danger confirmLabel="Ya, hapus" onConfirm={onDelete}
        description={del && <>Hapus <b>{del.name}</b> ({del.nik})? Anggota dengan simpanan atau pinjaman belum lunas tidak dapat dihapus.</>} />
      <MemberForm open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </div>
  );
}
